import Header from "../components/Header";
import styles from "../styles/About.module.css";
import { getYearsOfExperience } from "../utils/experience";

const cardContent = [
  {
    title: "Problem-driven",
    description:
      "Starting with real problems and building tools that fully solve them.",
    content:
      "I’m often frustrated by missing tools — or tools that only partially solve a problem and force me to combine multiple solutions. When that happens, I usually start with a rough idea and build a quick prototype, which then evolves into the final product.",
  },
  {
    title: "UI-first",
    description:
      "Designing flows before visuals to make software intuitive to use.",
    content:
      "I focus on user flow before visual design. A beautiful interface means little if the flow is confusing, so I aim to make interactions intuitive, predictable, and require as few steps as possible.",
  },
  {
    title: "Maintanable",
    description:
      "Building systems that evolve without constant developer involvement.",
    content:
      "I design products to be easy to maintain for both developers and end users. Especially in Salesforce, I focus on giving business users the ability to make changes themselves, reducing developer dependency and freeing time for meaningful improvements.",
  },
];

export default function About() {
  return (
    <>
      <Header
        title="About"
        subtitle="I’m a software engineer focused on building thoughtful tools for learning and data quality — with a strong emphasis on usability, clarity, and long-term maintainability."
      />
      <section className="section">
        <div className="container">
          <article className={`card ${styles.aboutTop}`}>
            <div className={styles.aboutTopText}>
              <p>
                I'm <strong>Krzysztof M. Chabowski</strong>, a professional{" "}
                <strong>Salesforce developer</strong> working across the
                platform — from <strong>Apex and integrations</strong> to{" "}
                <strong>Lightning Web Components</strong>.
              </p>
              <p>
                Separately, I also build applications with{" "}
                <strong>React</strong> and <strong>React Native</strong>. Many
                of my projects start when I notice a real problem and ask a
                simple question: How would I want this to work if I were the
                user? From there, I focus on usability and long-term
                maintainability.
              </p>
              <p>
                I enjoy creating software that genuinely makes everyday work
                easier. Whether it's simplifying complex processes or giving
                users a clear view of what's missing, I care about building
                tools that people find intuitive, helpful, and pleasant to use.
              </p>
            </div>

            <div className={styles.aboutTopPhoto}>
              <div className="photoCard">
                <img
                  className="photo"
                  src="/images/profile.jpg"
                  alt="Portrait of Krzysztof Chabowski"
                />
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className={styles.sectionTitle}>
            How I approach building software
          </h2>

          <div className={styles.stack}>
            {cardContent.map((card) => (
              <article className="card" key={card.title}>
                <header className="cardHeader">
                  <div className="cardHeaderText">
                    <h3 className="cardTitle">{card.title}</h3>
                    <p className="cardDescription">{card.description}</p>
                  </div>
                </header>

                <div className="divider" />

                <p className={styles.cardBody}>{card.content}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <article className="card">
            <header className="cardHeader">
              <div className="cardHeaderText">
                <h3 className="cardTitle">Experience</h3>
              </div>
            </header>
            <div className="divider" />
            <p className={styles.experienceIntro}>
              Professional software development experience across multiple
              industries, with a strong focus on frontend architecture and
              long-term system quality.
            </p>
            <ul className={`bulletList ${styles.experienceList}`}>
              <li className="bulletListItem">
                Professional Salesforce developer with {getYearsOfExperience()}+
                years of experience
              </li>
              <li className="bulletListItem">
                Experience across{" "}
                <strong>hospitality, consulting, education</strong>, and{" "}
                <strong>public utility</strong> sectors
              </li>
              <li className="bulletListItem">
                <strong>Lead frontend developer</strong>, responsible for
                architecture, guidelines, and code reviews
              </li>
              <li className="bulletListItem">
                Focus on <strong>UI-heavy, customer-facing applications</strong>
                , as well as internal employee tools
              </li>
              <li className="bulletListItem">
                Known for <strong>long-term thinking</strong>, anticipating
                architectural issues and system-wide impact
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <article className="card">
            <header className="cardHeader">
              <div className="cardHeaderText">
                <h3 className="cardTitle">Certificates</h3>
              </div>
            </header>

            <div className="divider" />

            <div className={styles.badges}>
              <img
                className={styles.badge}
                src="/images/badge-agentforce.png"
                alt="Certified Agentforce Specialist"
              />
              <img
                className={styles.badge}
                src="/images/badge-admin.png"
                alt="Certified Platform Administrator"
              />
              <img
                className={styles.badge}
                src="/images/badge-app-builder.png"
                alt="Certified Platform App Builder"
              />
            </div>
          </article>
        </div>
      </section>
    </>
  );
}
